import { useCallback, useEffect, useState } from 'react';
import { useGenericModal } from '../..';
import { EditorModal, EditorModalProps } from './editor-modal';

/**
 * PSTUDIO-3897 — the editor's content is owned here, as React state, and the
 * `code` prop only ever SEEDS it.
 *
 * It used to be passed straight through to `<CodeEditor value={code}>` while the
 * live text lived inside the web component, read back imperatively on Save via
 * `editorRef.current.getValue()`. That combination silently reverted edits:
 * `@lit/react`'s `createComponent` re-applies every property on EVERY parent
 * render (its `useLayoutEffect` has no dependency array and `setProperty`
 * deliberately skips dirty-checking properties), the callers pass a snapshot of
 * the persisted value (`brandingForm.getValues('customPypeCss')`), and
 * `@uiw/react-codemirror` replaces the whole document whenever its `value` prop
 * differs from the current doc. So any unrelated re-render while the modal was
 * open — an XState context update from a background load, for instance —
 * rewrote the user's in-progress text back to the last saved value, and Save
 * then persisted that.
 *
 * With the draft fed by `onChange`, the property written back on each render is
 * always what the component already holds, so Lit's own dirty check
 * short-circuits and CodeMirror is never re-dispatched.
 */
export const useEditorModal = ({
  onClose: _onClose,
  code,
  ...props
}: Partial<EditorModalProps>) => {
  const {
    modalRef,
    openModal: openGenericModal,
    closeModal,
  } = useGenericModal();

  const [draft, setDraft] = useState(code ?? '');

  // Re-seed only when `code` genuinely CHANGES — never on every render, which is
  // the whole bug. This is also why seeding can't live in `openModal` alone:
  // `pages/actions.tsx` opens the modal from a fetch callback and only sets
  // `code` on the following render, so the seed has to arrive with the value.
  useEffect(() => {
    setDraft(code ?? '');
  }, [code]);

  const openModal = useCallback(() => {
    // Fresh start per open, so edits abandoned via Cancel don't reappear.
    setDraft(code ?? '');
    openGenericModal();
  }, [code, openGenericModal]);

  const onClose = useCallback(() => {
    _onClose?.();
    closeModal();
  }, [_onClose, closeModal]);

  const editorModal = (
    <EditorModal
      {...props}
      code={draft}
      onCodeChange={setDraft}
      modalRef={modalRef}
      onClose={onClose}
    />
  );

  return {
    modalRef,
    editorModal,
    openModal,
    closeModal,
  };
};
